import { Text } from '@/components/ui';
import { resolveColor as resolveColorUtil } from '@/utils/colorUtils';

import { ComponentProps, ElementType, forwardRef, ReactNode } from 'react';

import {
  Button as RadixButton,
  IconButton as RadixIconButton,
  useThemeContext,
} from '@radix-ui/themes';
import type { RadixButtonProps, RadixIconButtonProps } from '@/types/radix-ui';
import clsx from 'clsx';

export type ButtonVariant =
  | 'contained'
  | 'outlined'
  | 'text'
  | 'link'
  | 'classic'
  | 'soft'
  | 'surface'
  | 'ghost';
export type ButtonColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'light'
  | 'destructive'
  | 'orange'
  | 'blue'
  | 'amber'
  | 'red'
  | 'green'
  | 'info';
export type ButtonSize = 'xsmall' | 'small' | 'medium' | 'large';

interface BaseButtonProps {
  asChild?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  isIconOnly?: boolean;
  color?: ButtonColor;
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
  justify?: boolean;
  ghost?: boolean;
  highContrast?: boolean;
  type?: string;
  labelProp?: React.ComponentProps<typeof Text>; // For Text component props like truncate, className, etc.

  // Polymorphic props
  as?: ElementType;
  component?: ElementType; // Material-UI compatibility
}

export type ButtonProps<T extends ElementType = 'button'> = BaseButtonProps &
  Omit<ComponentProps<T>, keyof BaseButtonProps> &
  Omit<
    RadixButtonProps & RadixIconButtonProps,
    'color' | 'size' | 'variant'
  > & {
    // Add support for polymorphic component props
    href?: string;
    to?: string;
    target?: string;
    rel?: string;
  };

const sizeMapping = {
  xsmall: '1',
  small: '2',
  medium: '3',
  large: '4',
} as const;

const variantMapping = {
  classic: 'classic',
  contained: 'solid',
  outlined: 'outline',
  text: 'outline',
  link: 'soft',
  soft: 'soft',
  ghost: 'ghost',
  surface: 'surface',
} as const;

function resolveColor(
  color: ButtonColor,
  variant: ButtonVariant,
  accentColor?: string
): { color: RadixButtonProps['color']; highContrast?: boolean } {
  // highContrast default false
  let highContrast = false;
  let radixColor: RadixButtonProps['color'];

  // Handle special cases first
  switch (color) {
    case 'light':
      radixColor = 'sky';
      break;
    case 'primary':
      radixColor = (accentColor as RadixButtonProps['color']) || 'cyan';
      break;
    case 'secondary':
    case 'default':
      radixColor = 'gray';
      // classic + secondary = highContrast
      if (variant === 'classic') {
        highContrast = true;
        radixColor = accentColor as RadixButtonProps['color'];
      }
      break;
    case 'info':
      radixColor = 'cyan';
      break;
    // Use centralized color mapping for other colors
    default:
      radixColor = resolveColorUtil<RadixButtonProps['color']>(
        color,
        accentColor
      );
      break;
  }

  // highContrast for some colors/variants
  if (color === 'primary' && variant === 'classic') highContrast = true;
  if (color === 'default' && variant === 'classic') highContrast = true;
  if (color === 'error' && variant === 'contained') highContrast = true;
  if (color === 'destructive' && variant === 'contained') highContrast = true;

  return { color: radixColor, highContrast };
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      as: Component = 'button',
      component, // Material-UI fallback
      variant = 'soft',
      color = 'default',
      size = 'medium',
      asChild = false,
      isIconOnly = false,
      className,
      startIcon,
      endIcon,
      children,
      type = 'button',
      highContrast,
      justify = false,
      fullWidth = false,
      labelProp,
      ...props
    },
    ref
  ) => {
    const Comp = isIconOnly ? RadixIconButton : RadixButton;
    // NEW: get accentColor from theme context
    const theme = useThemeContext();
    const accentColor = theme?.accentColor || 'cyan';

    const ActualComponent = Component || component || 'button';

    const renderContent = () => {
      if (isIconOnly) {
        return startIcon || endIcon || children;
      }

      const content = [];

      if (startIcon) {
        content.push(
          <span key="start-icon" className="button__icon button__icon--start">
            {startIcon}
          </span>
        );
      }

      if (children) {
        content.push(
          <Text
            key="label"
            size="inherit"
            className="button__label"
            as="span"
            {...labelProp}
          >
            {children}
          </Text>
        );
      }

      if (endIcon) {
        content.push(
          <span key="end-icon" className="button__icon button__icon--end">
            {endIcon}
          </span>
        );
      }

      return content;
    };

    const radixSize = sizeMapping[size as NonNullable<BaseButtonProps['size']>];
    const radixVariant =
      variantMapping[variant as NonNullable<BaseButtonProps['variant']>];
    // NEW: pass accentColor to resolveColor
    const { color: resolvedColor, highContrast: resolvedContrast } =
      resolveColor(color as ButtonColor, variant as ButtonVariant, accentColor);

    const computedClassName = clsx(
      'button',
      `button-size-${size}`,
      `button-color-${color}`,
      `button-variant-${variant}`,
      className,
      {
        'button-fullWidth': fullWidth,
        'button-justify': justify,
      }
    );

    const commonProps = {
      ref,
      className: computedClassName,
      color: resolvedColor as RadixButtonProps['color'],
      size: radixSize,
      variant: radixVariant,
      highContrast:
        typeof highContrast === 'boolean' ? highContrast : resolvedContrast,
      asChild,
      ...props,
    };

    if (asChild) {
      return (
        <Comp {...commonProps} asChild>
          {children}
        </Comp>
      );
    }

    if (ActualComponent !== 'button') {
      return (
        <Comp {...commonProps} asChild>
          <ActualComponent type={type} {...props}>
            {renderContent()}
          </ActualComponent>
        </Comp>
      );
    }

    return (
      <Comp {...commonProps} type={type}>
        {renderContent()}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button };
